import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge, Spinner, Table, Card } from "react-bootstrap";

import { API_URL } from "../../services/api";
import axios from "axios";
import "./Dashboard.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatRupiah = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(value);
};

const StatusBadge = ({ status }) => {
  if (!status) return <Badge bg="secondary">-</Badge>;
  const s = status.toLowerCase();
  if (s === "terbayar") return <Badge bg="success">{status}</Badge>;
  if (s === "disetujui")
    return (
      <Badge bg="warning" text="dark">
        {status}
      </Badge>
    );
  if (s === "pengajuan") return <Badge bg="info">{status}</Badge>;
  if (s.includes("batal")) return <Badge bg="danger">{status}</Badge>;
  return <Badge bg="secondary">{status}</Badge>;
};

const DetailTagihan = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null); // invoice header info saved in localStorage
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Guard against React Strict Mode double-invocation
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Read id_inv (and optionally invoice header info) from localStorage token
    const raw = localStorage.getItem(`_tinv_${token}`);
    localStorage.removeItem(`_tinv_${token}`);

    if (!raw) {
      setError(
        "Link tidak valid atau sudah kadaluarsa. Silakan buka kembali dari halaman daftar tagihan.",
      );
      setLoading(false);
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      setError(
        "Data tidak dapat dibaca. Silakan buka kembali dari halaman daftar tagihan.",
      );
      setLoading(false);
      return;
    }

    setInvoice(parsed);

    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/tagihan/detail/${parsed.id}`);
        if (res.data.success) {
          setData(res.data.data || []);
        } else {
          setError("Data tidak ditemukan");
        }
      } catch (err) {
        setError(
          "Gagal memuat detail: " +
            (err.response?.data?.message || err.message),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [token]);

  const totalHarga = data.reduce((sum, r) => sum + (r.harga || 0), 0);

  return (
    <div>
      {/* Page header */}
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <button
              onClick={() => navigate(-1)}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.4)",
                color: "#fff",
                borderRadius: 6,
                padding: "6px 14px",
                cursor: "pointer",
                marginRight: 12,
                fontSize: "0.85rem",
              }}
            >
              ← Kembali
            </button>
            <div className="page-header-icon">
              <i className="fas fa-file-invoice-dollar"></i>
            </div>
            <div>
              <h1 className="page-header-title">
                Detail Invoice{invoice ? ` — ${invoice.nomor}` : ""}
              </h1>
              <p className="page-header-subtitle">
                Rincian pengajuan dalam invoice ini
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice header info */}
      {invoice && (
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body>
            <div className="row g-3">
              <div className="col-sm-4">
                <div className="text-muted small">Nomor Invoice</div>
                <div className="fw-semibold">{invoice.nomor || "-"}</div>
              </div>
              <div className="col-sm-4">
                <div className="text-muted small">Tanggal</div>
                <div>{formatDate(invoice.tanggal)}</div>
              </div>
              <div className="col-sm-4">
                <div className="text-muted small">Status Pembayaran</div>
                <div>
                  <StatusBadge status={invoice.status_payment} />
                </div>
              </div>
              {invoice.tgl_transfer && (
                <div className="col-sm-4">
                  <div className="text-muted small">Tanggal Transfer</div>
                  <div>{formatDate(invoice.tgl_transfer)}</div>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body>
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Memuat detail invoice...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-5">
              <i className="fas fa-exclamation-circle fa-3x text-danger mb-3 d-block"></i>
              <p className="text-danger">{error}</p>
            </div>
          )}

          {!loading && !error && data.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
              <p className="text-muted">
                Tidak ada data pengajuan dalam invoice ini.
              </p>
            </div>
          )}

          {!loading && !error && data.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="small text-muted">
                  {data.length} pengajuan dalam invoice ini
                </span>
                <span className="fw-semibold">
                  Total: {formatRupiah(totalHarga)}
                </span>
              </div>
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 40 }}>No</th>
                      <th>No. Daftar</th>
                      <th>Tgl. Daftar</th>
                      <th>Nama PU</th>
                      <th>Pendamping</th>
                      <th>Harga</th>
                      <th>Rekening</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, idx) => (
                      <tr key={row.id_reg}>
                        <td className="text-muted">{idx + 1}</td>
                        <td>
                          <span className="fw-semibold">
                            {row.no_daftar || "-"}
                          </span>
                        </td>
                        <td>{formatDate(row.tgl_daftar)}</td>
                        <td>{row.nama_pu || "-"}</td>
                        <td>{row.nama_pendamping || "-"}</td>
                        <td className="text-end">{formatRupiah(row.harga)}</td>
                        <td>
                          <div style={{ fontSize: "0.78rem" }}>
                            <span className="d-block">
                              {row.kode_bank || "-"}
                            </span>
                            <span className="text-muted">
                              {row.no_rekening || "-"}
                            </span>
                            {row.nama_rekening && (
                              <span className="d-block text-muted">
                                a/n {row.nama_rekening}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan={5} className="text-end fw-semibold">
                        Total
                      </td>
                      <td className="text-end fw-semibold">
                        {formatRupiah(totalHarga)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DetailTagihan;
